import React, { useEffect, useRef, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import { axiosInstance } from "contexts/axiosApiInstance";
import { UserAccountResponse } from "components/entities";
import {
  AvatarInitial,
  invalidatePhotoCache,
} from "components/common/AvatarInitial";
import { EditableName } from "components/canvas/utils/EditableName";
import { ImageRow } from "components/common/ImageRow";
import { createLogger } from "debug/DebugingLogger";
import { showNotificationGlobal } from "stores/useNotificationStore";

const log = createLogger("SettingsDialog");

interface ProfileHeaderProps {
  accountDetails: UserAccountResponse | undefined;
  email: string;
  onAccountUpdated: () => void;
}

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  accountDetails,
  email,
  onAccountUpdated,
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [showSavedTick, setShowSavedTick] = useState(false);
  const savedTickTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  const savedName = accountDetails?.account_details?.name || "";
  const currentPhoto = accountDetails?.account_details?.photo || "";

  useEffect(
    () => () => {
      if (savedTickTimer.current) clearTimeout(savedTickTimer.current);
    },
    [],
  );

  const photoChanged = () => {
    invalidatePhotoCache();
    onAccountUpdated();
  };

  // `EditableName.commit` is called synchronously and inspects the return value,
  // so this must stay sync — fire the PUT off and handle its result later.
  const commitName = (name: string): void => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === savedName) return;
    void axiosInstance
      .put("/action/user/display-name", { name: trimmed })
      .then(() => {
        setShowSavedTick(true);
        if (savedTickTimer.current) clearTimeout(savedTickTimer.current);
        savedTickTimer.current = setTimeout(
          () => setShowSavedTick(false),
          2000,
        );
        onAccountUpdated();
      })
      .catch((error: unknown) => {
        log.error("Failed to update display name: %o", error);
        showNotificationGlobal("Failed to update display name", "error");
      });
  };

  return (
    <ImageRow
      className="settings-profile"
      picture={
        <AvatarInitial
          name={savedName}
          icon={currentPhoto}
          color={accountDetails?.account_details?.color || ""}
          clientId={accountDetails?.account_details?.id}
          size="md"
        />
      }
      hasImage={!!currentPhoto}
      noun="photo"
      label={currentPhoto ? "Change photo" : "Upload photo"}
      onUpload={(photo) =>
        axiosInstance.put("/action/user/photo", { photo }).then(photoChanged)
      }
      onRemove={() =>
        axiosInstance.delete("/action/user/photo").then(photoChanged)
      }
    >
      <div className="settings-name">
        <EditableName
          name={savedName || "Not set"}
          isEditing={isEditingName}
          canEdit={true}
          editTrigger="click"
          setIsEditing={setIsEditingName}
          update={() => {}}
          commit={commitName}
          abort={() => setIsEditingName(false)}
          editColor="var(--color-accent)"
          ariaLabel="Display name"
          maxLength={100}
        />
        {!isEditingName && showSavedTick && (
          <CheckIcon
            sx={{ fontSize: 14, color: "var(--color-success)" }}
            aria-label="Saved"
          />
        )}
      </div>
      <span className="settings-email">{email}</span>
    </ImageRow>
  );
};
